import { makeAutoObservable, runInAction } from "mobx";
import {
  superadminGetClient,
  superadminGetClientStats,
} from "@pro-sto/api-client";
import type {
  SuperadminClientDetailDto as ClientDetail,
  ClientStatsResponseDto as ClientStats,
} from "@pro-sto/api-client";
import { toastStore } from "../ToastStore";
import { TransactionsSubStore } from "./TransactionsSubStore";
import { BookingsSubStore } from "./BookingsSubStore";
import { PaymentDetailSubStore } from "./PaymentDetailSubStore";

class ClientDetailStore {
  // ─── Header ───
  client: ClientDetail | null = null;
  isLoading = true;

  // ─── Stats ───
  stats: ClientStats | null = null;
  statsYear = new Date().getFullYear();
  isStatsLoading = false;

  // ─── Sub-stores ───
  tx: TransactionsSubStore;
  bk: BookingsSubStore;
  pd: PaymentDetailSubStore;

  constructor() {
    const getUuid = () => this.uuid;
    this.tx = new TransactionsSubStore(getUuid);
    this.bk = new BookingsSubStore(getUuid);
    this.pd = new PaymentDetailSubStore();
    makeAutoObservable(this, {
      tx: false,
      bk: false,
      pd: false,
    });
  }

  // ─── Getters ───

  get uuid(): string {
    return this.client?.uuid ?? "";
  }

  // ─── Header ───

  loadClient = async (uuid: string) => {
    this.isLoading = true;
    try {
      const data = await superadminGetClient({ uuid });
      runInAction(() => {
        this.client = data;
        this.isLoading = false;
      });
    } catch {
      runInAction(() => {
        this.isLoading = false;
      });
      toastStore.showError("Не удалось загрузить данные клиента");
    }
  };

  // ─── Stats ───

  setStatsYear = (year: number) => {
    const currentYear = new Date().getFullYear();
    if (year > currentYear) return;
    this.statsYear = year;
    if (this.uuid) this.loadStats();
  };

  loadStats = async (uuidOverride?: string) => {
    const id = uuidOverride ?? this.uuid;
    if (!id) return;
    this.isStatsLoading = true;
    try {
      const data = await superadminGetClientStats({
        uuid: id,
        year: this.statsYear,
      });
      runInAction(() => {
        this.stats = data;
        this.isStatsLoading = false;
      });
    } catch {
      runInAction(() => {
        this.isStatsLoading = false;
      });
      toastStore.showError("Не удалось загрузить метрики");
    }
  };

  // ─── Reset ───

  reset = () => {
    this.client = null;
    this.isLoading = true;
    this.stats = null;
    this.statsYear = new Date().getFullYear();
    this.tx.reset();
    this.bk.reset();
    this.pd.reset();
  };
}

export const clientDetailStore = new ClientDetailStore();
export type { ClientDetailStore };

export type {
  ClientDetail,
  ClientStats,
  TransactionItem,
  BookingItem,
  PaginationMeta,
  PaymentDetail,
  PaymentEvent,
} from "./types";
